import React, { useState } from "react";
import { Shield, Globe, Lock, Check } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/AuthContext";
import { formatError } from "../lib/api";

const OPTIONS = [
  {
    key: "shared",
    label: "Shared",
    desc: "Anyone with the QR can view. Read-only for people outside your household.",
    icon: Globe,
  },
  {
    key: "private",
    label: "Private",
    desc: "Only your household can see your containers. Outside scanners get locked out.",
    icon: Lock,
  },
];

export const PrivacyDefaultCard = () => {
  const { user, updateMe } = useAuth();
  // Legacy "case_by_case" reads as "shared" for display since the option was
  // retired in favor of the separate Personal Storing toggle.
  const raw = user?.default_container_visibility || "shared";
  const current = raw === "case_by_case" ? "shared" : raw;
  const [busy, setBusy] = useState(null);

  const choose = async (key) => {
    if (key === current || busy) return;
    setBusy(key);
    try {
      await updateMe({ default_container_visibility: key });
      toast.success(`New containers will default to ${key}`);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mt-4 tup-card p-5" data-testid="privacy-default-card">
      <div className="flex items-center gap-3 mb-1">
        <div className="w-10 h-10 rounded-xl bg-[#DCEDF0] flex items-center justify-center shrink-0">
          <Shield className="w-5 h-5 text-[#1F5566]" />
        </div>
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-[#8A93A6]">
            Preferences
          </div>
          <div className="font-heading text-lg font-semibold text-[#2D3142]">
            Privacy default
          </div>
        </div>
      </div>

      <ul className="mt-4 space-y-2" data-testid="privacy-default-options">
        {OPTIONS.map((opt) => {
          const active = current === opt.key;
          const Icon = opt.icon;
          return (
            <li key={opt.key}>
              <button
                type="button"
                onClick={() => choose(opt.key)}
                disabled={busy !== null}
                data-testid={`privacy-default-${opt.key}`}
                aria-pressed={active}
                className={`w-full text-left rounded-2xl p-3 border-2 transition-all flex items-start gap-3 ${
                  active
                    ? "border-[#277A82] bg-[#DCEDF0]/40"
                    : "border-[#E2E0D8] bg-white hover:border-[#277A82]/60"
                } ${busy !== null && !active ? "opacity-60" : ""}`}
              >
                <div
                  className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${
                    active ? "brand-gradient-bg" : "bg-[#F5F3ED]"
                  }`}
                >
                  <Icon className={`w-4 h-4 ${active ? "text-white" : "text-[#1F5566]"}`} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span
                      className={`font-semibold text-sm ${
                        active ? "text-[#1F5566]" : "text-[#2D3142]"
                      }`}
                    >
                      {opt.label}
                    </span>
                    {active && (
                      <span className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-[#277A82]">
                        <Check className="w-3 h-3" /> Active
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-[#4F5D75] mt-1 leading-snug">{opt.desc}</p>
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
